import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Search, Filter, X, Eye, Download, ChevronLeft, ChevronRight, Printer, Trash2, AlertTriangle, Edit } from 'lucide-react';
import ReactDOM from 'react-dom';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPageHeading } from '../../locales/navigation';
import { getAppUi } from '../../locales/appUi';

const DeliveryNotes = () => {
  const { language } = useLanguage();
  const ui = useMemo(() => getAppUi(language), [language]);
  const navigate = useNavigate();
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    DriverId: '',
    Status: '',
    Keyword: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Load drivers for filter
  const loadDrivers = async () => {
    try {
      const response = await MainService.DriversInDriverAllocation();
      if (response && Array.isArray(response)) {
        setDrivers(response);
      }
    } catch (err) {
      console.error('Error loading drivers:', err);
    }
  };

  // Load delivery notes
  const loadDeliveryNotes = async (page = 1, applyFilters = false) => {
    try {
      setLoading(true);
      setError('');

      const skip = (page - 1) * pageSize;
      const requestData = {
        Skip: skip,
        Take: pageSize
      };

      // Only add filter parameters if filters are applied
      if (applyFilters || filtersApplied) {
        if (filters.DriverId) {
          requestData.DriverId = parseInt(filters.DriverId);
        }
        if (filters.Status) {
          requestData.Status = filters.Status;
        }
        if (filters.Keyword) {
          requestData.Keyword = filters.Keyword;
        }
      }

      const response = await MainService.DeliveryNotesInList(requestData);

      if (response && response.Data) {
        setDeliveryNotes(response.Data);
        setTotalCount(response.Total || 0);
        setCurrentPage(page);
      } else {
        setError('Failed to load delivery notes');
      }
    } catch (err) {
      console.error('Error loading delivery notes:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
    loadDeliveryNotes(1, false);
  }, []);

  const handleApplyFilters = () => {
    setFiltersApplied(true);
    setCurrentPage(1);
    loadDeliveryNotes(1, true);
  };

  const handleClearFilters = () => {
    setFilters({
      DriverId: '',
      Status: '',
      Keyword: ''
    });
    setFiltersApplied(false);
    setCurrentPage(1);
    loadDeliveryNotes(1, false);
  };

  const handleSearch = () => {
    handleApplyFilters();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadDeliveryNotes(newPage, filtersApplied);
    }
  };

  const handleView = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.GetDeliveryNoteById(note.Id);
      if (response) {
        setSelectedNote(response);
        setViewModalOpen(true);
      } else {
        setError('Failed to load delivery note details');
      }
    } catch (err) {
      console.error('Error loading delivery note:', err);
      setError('Failed to load delivery note details');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (note) => {
    try {
      setLoading(true);
      const response = await MainService.PrintDeliveryNoteAllocation(note.Id);
      if (response && response != null) {
        const printUrl = `${getApiBaseUrl()}ReportFiles/${response}`;
        window.open(printUrl, '_blank');
        // Don't show success message for print - the print window opening is enough feedback
      } else {
        setError(ui('delivery.notes.printError'));
      }
    } catch (err) {
      console.error('Error printing delivery note:', err);
      setError(ui('delivery.notes.printError'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (note) => {
    // Navigate to Delivery Allocation page with delivery note ID
    navigate('/home/delivery/allocation', { 
      state: { deliveryNoteId: note.Id, driverId: note.DriverId } 
    });
  };

  const handleDelete = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!noteToDelete) return;

    // Check if delivery note status is FINISHED
    if (noteToDelete.Status === 'FINISHED') {
      setError('Cannot delete - Delivery note is already finished');
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await MainService.DeleteDeliveryNote(noteToDelete.Id);
      if (response) {
        setSuccess('Delivery note deleted successfully');
        await loadDeliveryNotes(currentPage, filtersApplied);
      } else {
        setError('Failed to delete delivery note');
      }
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (err) {
      console.error('Error deleting delivery note:', err);
      setError('Something went wrong');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  const exportToExcel = () => {
    if (deliveryNotes.length === 0) {
      setError('No data to export');
      return;
    }

    try {
      // Prepare data for export
      const exportData = deliveryNotes.map((note, index) => ({
        'S.No': (currentPage - 1) * pageSize + index + 1,
        'Ref No': note.RefNo || '',
        'Date': formatDate(note.Date),
        'Driver': note.Driver?.Name || '',
        'Vehicle': note.Vehicle ? `${note.Vehicle.Number}${note.Vehicle.Model ? ` (${note.Vehicle.Model})` : ''}` : '',
        'Status': note.Status || '',
        'Remarks': note.Remarks || '',
        'Created By': note.CreatedName || ''
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Delivery Notes');

      // Set column widths
      const colWidths = [
        { wch: 8 },  // S.No
        { wch: 15 }, // Ref No
        { wch: 12 }, // Date
        { wch: 20 }, // Driver
        { wch: 20 }, // Vehicle
        { wch: 12 }, // Status
        { wch: 30 }, // Remarks
        { wch: 15 }  // Created By
      ];
      ws['!cols'] = colWidths;

      // Generate filename with current date
      const fileName = `Delivery_Notes_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Save file
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file downloaded successfully');
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError('Failed to export to Excel');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const statusColors = {
    'CONFIRMED': 'bg-blue-100 text-blue-800',
    'NEW': 'bg-yellow-100 text-yellow-800',
    'FINISHED': 'bg-green-100 text-green-800',
    'CANCELLED': 'bg-red-100 text-red-800',
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'IN_PROGRESS': 'bg-blue-100 text-blue-800'
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startRecord = deliveryNotes.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{getPageHeading(language, 'deliveryNotes')}</h1>
            <p className="text-sm text-gray-500 mt-1">{ui('delivery.notes.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowFilters(!showFilters);
              }}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showFilters
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              {ui('common.filters')}
            </button>
            <button
              onClick={exportToExcel}
              disabled={deliveryNotes.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {ui('common.exportExcel')}
            </button>
            <button
              onClick={() => loadDeliveryNotes(currentPage, filtersApplied)}
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              {ui('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Toast Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters Section */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.driver')}</label>
              <select
                value={filters.DriverId}
                onChange={(e) => setFilters(prev => ({ ...prev, DriverId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{ui('delivery.notes.allDrivers')}</option>
                {drivers.map(driver => (
                  <option key={driver.Id} value={driver.Id}>{driver.Name}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('common.status')}</label>
              <select
                value={filters.Status}
                onChange={(e) => setFilters(prev => ({ ...prev, Status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{ui('delivery.notes.allStatus')}</option>
                <option value="CONFIRMED">{ui('delivery.notes.statusConfirmed')}</option>
                <option value="NEW">{ui('delivery.notes.statusNew')}</option>
                <option value="FINISHED">{ui('delivery.notes.statusFinished')}</option>
              </select>
            </div>
            <div className="md:col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">{ui('delivery.notes.searchRefLabel')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.Keyword}
                  onChange={(e) => setFilters(prev => ({ ...prev, Keyword: e.target.value }))}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder={ui('delivery.notes.searchRefPlaceholder')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:col-span-2 flex items-end gap-2">
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex-1"
              >
                {ui('common.apply')}
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex-1"
              >
                {ui('common.clear')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Notes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Table Header */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">{ui('delivery.notes.listTitle')}</h2>
            {totalCount > 0 && (
              <span className="text-sm text-gray-600">
                {ui('delivery.notes.totalCount', { total: totalCount })}
              </span>
            )}
          </div>
        </div>

        <div className="overflow-x-auto" style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.sNo')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.refNo')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.driver')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.vehicle')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.remarks')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.createdBy')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{ui('columns.options')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && deliveryNotes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">{ui('delivery.notes.loading')}</p>
                  </td>
                </tr>
              ) : deliveryNotes.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    <p className="text-lg mb-2">{ui('delivery.notes.noNotes')}</p>
                    <p className="text-sm">{ui('delivery.notes.noNotesHint')}</p>
                  </td>
                </tr>
              ) : (
                deliveryNotes.map((note, index) => (
                  <tr key={note.Id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {startRecord + index}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{note.RefNo || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatDate(note.Date)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{note.Driver?.Name || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {note.Vehicle ? `${note.Vehicle.Number}${note.Vehicle.Model ? ` (${note.Vehicle.Model})` : ''}` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        statusColors[note.Status] || 'bg-gray-100 text-gray-800'
                      }`}>
                        {note.Status || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={note.Remarks || ''}>
                      {note.Remarks || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{note.CreatedName || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleView(note)}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title={ui('common.viewDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(note)}
                          className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                          title={ui('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrint(note)}
                          disabled={loading}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title={ui('common.print')}
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        {note.Status !== 'FINISHED' && (
                          <button
                            onClick={() => handleDelete(note)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title={ui('common.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Table Footer - Pagination */}
        {deliveryNotes.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {ui('delivery.notes.showingToOf', { from: startRecord, to: endRecord, total: totalCount })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={ui('common.previousPage')}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          currentPage === pageNum
                            ? 'bg-primary text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={ui('common.nextPage')}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewModalOpen && selectedNote && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-xl font-semibold text-gray-900">{ui('delivery.notes.detailsTitle')}</h3>
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.refNo')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.RefNo || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.date')}</label>
                  <p className="text-sm text-gray-900">{formatDate(selectedNote.Date)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.driver')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.Driver?.Name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.vehicle')}</label>
                  <p className="text-sm text-gray-900">
                    {selectedNote.Vehicle ? `${selectedNote.Vehicle.Number}${selectedNote.Vehicle.Model ? ` (${selectedNote.Vehicle.Model})` : ''}` : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('common.status')}</label>
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                    statusColors[selectedNote.Status] || 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedNote.Status || '-'}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.createdBy')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.CreatedName || '-'}</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{ui('columns.remarks')}</label>
                  <p className="text-sm text-gray-900">{selectedNote.Remarks || '-'}</p>
                </div>
              </div>
              {selectedNote.DeliveryNoteDetails && selectedNote.DeliveryNoteDetails.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">{ui('delivery.notes.ordersInNote', { count: selectedNote.DeliveryNoteDetails.length })}</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.voucherNo')}</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.customer')}</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-700">{ui('columns.amount')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedNote.DeliveryNoteDetails.map((detail) => (
                          <tr key={detail.Id}>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.VoucherNo || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">{detail.Order?.Customer?.Name || '-'}</td>
                            <td className="px-4 py-2 text-gray-600">
                              {detail.Order?.CustomerAmount !== null && detail.Order?.CustomerAmount !== undefined 
                                ? `AED ${parseFloat(detail.Order.CustomerAmount).toFixed(2)}` 
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => {
                  setViewModalOpen(false);
                  setSelectedNote(null);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {ui('common.close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && ReactDOM.createPortal(
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 relative z-[100000]">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 -mx-6 -mt-6 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">{ui('delivery.notes.confirmDeleteTitle')}</h3>
              </div>
            </div>
            <div className="py-4">
              <p className="text-gray-700 mb-2">
                {ui('delivery.notes.confirmDeleteMessage', { refNo: noteToDelete?.RefNo || 'N/A' })}
              </p>
              <p className="text-sm text-gray-500">
                {ui('delivery.notes.confirmDeleteWarning')}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={cancelDelete}
                disabled={loading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ui('common.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>{ui('delivery.notes.deleting')}</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>{ui('common.delete')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DeliveryNotes;
